import React, { useState, useEffect, useRef } from "react";
import useGlobalInventory from "../../../services/Inventory/useGlobalInventory";
import useInventory from "../../../services/Inventory/useInventory";
import useRack from "../../../services/Inventory/useRack";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useContext } from "react";
import { GlobalContext } from "../../../Context/GlobalContext";
import useCatalogue from "../../../services/Order/UseCatalogue";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { ChevronDown } from "lucide-react";

const AddCatalogueItem = () => {
  const { fetchAllInventoryVariants, variants } = useGlobalInventory();
  const {
    bulkItems,
    bulkLoading,
    getSubCategories,
    handleBulkSubmit,
    setBulkItems,
    addItemVariant,
    uploadItemImage,
    insertCatalogItem,
  } = useInventory();

const {
  extractChaafoMenuFromImage,
  fetchSizeMaster,
  fetchUomMaster,
} = useCatalogue();
  const { fetchRacks, rows: racks } = useRack();
  const { userID } = useContext(GlobalContext);
  const navigate = useNavigate();

  const [mode, setMode] = useState("single");
  const [subCategories, setSubCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [csvErrors, setCsvErrors] = useState([]);

  const extractImageInputRef = useRef(null);
  const [extractingImage, setExtractingImage] = useState(false);
  const [extractedCatalogueItems, setExtractedCatalogueItems] = useState([]);
  const [extractErrors, setExtractErrors] = useState([]);
  const [uploadedExtractImageNames, setUploadedExtractImageNames] = useState(
    [],
  );

  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
const [categorySearch, setCategorySearch] = useState("");

const [showSubcategoryDropdown, setShowSubcategoryDropdown] = useState(false);
const [subcategorySearch, setSubcategorySearch] = useState("");

const [uomOptions, setUomOptions] = useState([]);
const [sizeOptions, setSizeOptions] = useState([]);
const [uomLoading, setUomLoading] = useState(false);
const [sizeLoading, setSizeLoading] = useState(false);

  const [formData, setFormData] = useState({
    itemName: "",
    status: "Y",
    rating: "",
    description: "",
    tags: "",
    photo: "",
    vegIcon: "",
    category: "",
    subcategory: "",
    rack: "",
    variant: "",
    uom: "",
    size: "",
    price: "",
    discount: "",
    discountedPrice: "",
    barcode: "",
    expiryDate: "",
    homeDeliveryPrice: "",
    takeawayPrice: "",
    diningPrice: "",
    roomOrderPrice: "",
    carOrderPrice: "",
  });

const getOptionValue = (item) =>
  item?.uom_code || item?.size_code || item?.code || item?.value || "";

const getOptionLabel = (item) =>
  item?.uom_name ||
  item?.size_name ||
  item?.name ||
  item?.label ||
  item?.value ||
  "-";

    const loadUomMaster = async () => {
      try {
        setUomLoading(true);

        const data = await fetchUomMaster("0");

        setUomOptions(data || []);
      } catch (error) {
        console.error("UOM load error:", error);
      } finally {
        setUomLoading(false);
      }
    };

useEffect(() => {
  loadSubCategories();
  fetchRacks();
  fetchAllInventoryVariants();
  loadUomMaster();
}, []);

const handleUomChange = async (e) => {
  const selectedUom = e.target.value;

  setFormData((prev) => ({
    ...prev,
    uom: selectedUom,
    size: "",
  }));

  setSizeOptions([]);

  if (!selectedUom) return;

  try {
    setSizeLoading(true);

    const data = await fetchSizeMaster(selectedUom);

    setSizeOptions(data || []);
  } catch (error) {
    console.error("Size load error:", error);
    toast.error("Failed to fetch size data");
  } finally {
    setSizeLoading(false);
  }
};

  const loadSubCategories = async () => {
    const data = await getSubCategories("");
    setSubCategories(data || []);
  };

  useEffect(() => {
    if (subCategories?.length) {
      const unique = [
        ...new Set(
          subCategories
            .map((s) => s.category?.trim())
            .filter((c) => c && c !== ""),
        ),
      ];
      setCategories(unique);
    }
  }, [subCategories]);

const sanitizeText = (value) => {
  return clean(value).replace(/\s+/g, " ").trim();
};

const sanitizeAlphaNumeric = (value) => {
  return clean(value)
    .replace(/[^a-zA-Z0-9]/g, "")
    .trim();
};

const sanitizePrice = (value) => {
  const raw = clean(value);

  if (!raw) return "";

  return raw.replace(/[^0-9.]/g, "");
};

  const sanitizeDate = (value) => {
    const raw = clean(value);

    if (!raw) return "";

    // Excel can export a date as a serial number, e.g. 72716.
    if (/^\d{4,5}$/.test(raw)) {
      const serial = Number(raw);
      const excelEpoch = Date.UTC(1899, 11, 30);
      const date = new Date(excelEpoch + serial * 24 * 60 * 60 * 1000);

      if (!Number.isNaN(date.getTime())) {
        const yyyy = date.getUTCFullYear();
        const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
        const dd = String(date.getUTCDate()).padStart(2, "0");
        return `${yyyy}/${mm}/${dd}`;
      }
    }

    const normalized = raw
      .replace(/-/g, "/")
      .replace(/[^0-9/]/g, "");

    // Accept YYYY/MM/DD.
    if (/^\d{4}\/\d{2}\/\d{2}$/.test(normalized)) {
      return normalized;
    }

    // Convert DD/MM/YYYY to YYYY/MM/DD.
    const dayFirstMatch = normalized.match(
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/,
    );

    if (dayFirstMatch) {
      const [, dd, mm, yyyy] = dayFirstMatch;
      return `${yyyy}/${mm.padStart(2, "0")}/${dd.padStart(2, "0")}`;
    }

    return "";
  };

  const validateBulkItems = (items) => {
    const errors = [];

    items.forEach((item, index) => {
      const rowNumber = index + 2;

      if (!item.itemName) {
        errors.push(`Row ${rowNumber}: itemName is required`);
      }

      if (!item.price) {
        errors.push(`Row ${rowNumber}: price is required`);
      }

      if (item.price && isNaN(Number(item.price))) {
        errors.push(
          `Row ${rowNumber}: price must be a number. Found "${item.price}"`,
        );
      }

      if (item.discountPrice && isNaN(Number(item.discountPrice))) {
        errors.push(
          `Row ${rowNumber}: discountPrice must be a number. Found "${item.discountPrice}"`,
        );
      }

      if (item.iconDish && !["Y", "N"].includes(item.iconDish.toUpperCase())) {
        errors.push(`Row ${rowNumber}: iconDish should be Y or N`);
      }
    });

    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "price" || name === "discount") {
      const updated = { ...formData, [name]: value };
      const price = parseFloat(updated.price || 0);
      const discount = parseFloat(updated.discount || 0);

      updated.discountedPrice = price > 0 ? Math.max(price - discount, 0) : "";

      setFormData(updated);
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onloadend = async () => {
      try {
        const base64 = reader.result;

        const res = await uploadItemImage({
          item_name: formData.itemName || `item_${Date.now()}`,
          image_data: base64,
        });

        //  FIXED CONDITION
        if (res?.status === "0" && res?.image_url) {
          setFormData((prev) => ({
            ...prev,
            photo: res.image_url,
          }));

          toast.success("Image uploaded ");
        } else {
          toast.error("Image upload failed ");
        }
      } catch (err) {
        console.error(err);
        toast.error("Upload error ");
      }
    };

    reader.readAsDataURL(file);
  };

  const clean = (val) => {
    if (val === null || val === undefined) return "";

    return String(val)
      .replace(/^\uFEFF/, "") // remove BOM from Excel CSV
      .replace(/\0/g, "") // remove null chars
      .trim();
  };

  const parseCSV = (text) => {
    const rows = [];
    let row = [];
    let cell = "";
    let inQuotes = false;

    const value = String(text || "").replace(/^\uFEFF/, "");

    for (let i = 0; i < value.length; i++) {
      const char = value[i];
      const nextChar = value[i + 1];

      if (char === '"') {
        // Escaped quote inside quoted value: "" => "
        if (inQuotes && nextChar === '"') {
          cell += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
        continue;
      }

      // Comma outside quotes means next column
      if (char === "," && !inQuotes) {
        row.push(clean(cell));
        cell = "";
        continue;
      }

      // New line outside quotes means next row
      if ((char === "\n" || char === "\r") && !inQuotes) {
        // handle Windows CRLF
        if (char === "\r" && nextChar === "\n") {
          i++;
        }

        row.push(clean(cell));

        const hasAnyValue = row.some((v) => clean(v) !== "");
        if (hasAnyValue) rows.push(row);

        row = [];
        cell = "";
        continue;
      }

      cell += char;
    }

    // last cell / row
    row.push(clean(cell));
    const hasAnyValue = row.some((v) => clean(v) !== "");
    if (hasAnyValue) rows.push(row);

    return rows;
  };

  const normalizeHeader = (header) => {
    return clean(header).toLowerCase().replace(/\s+/g, "").replace(/_/g, "");
  };

  const getValue = (row, keys, fallback = "") => {
    for (const key of keys) {
      const normalizedKey = normalizeHeader(key);
      if (row[normalizedKey] !== undefined && row[normalizedKey] !== null) {
        return clean(row[normalizedKey]);
      }
    }
    return fallback;
  };

  const normalizePrice = (value) => {
    return sanitizePrice(value);
  };

  const normalizeExpiryDate = (value) => {
    return sanitizeDate(value);
  };

const parseExtractApiResponse = (apiResponse) => {
  try {
    const outputText = apiResponse?.output?.[0]?.content?.find(
      (contentItem) => contentItem?.type === "output_text",
    )?.text;

    if (!outputText) {
      toast.error("Extracted response text not found");
      return [];
    }

    const cleanedOutputText = outputText
      .replace(/^```json\s*/i, "")
      .replace(/^```\s*/i, "")
      .replace(/\s*```$/i, "")
      .trim();

    const parsedResponse = JSON.parse(cleanedOutputText);

    const extractedCategories = Array.isArray(parsedResponse?.categories)
      ? parsedResponse.categories
      : [];

    return extractedCategories.flatMap((category, categoryIndex) => {
      const categoryName = sanitizeText(category?.categoryName || "");

      const categoryItems = Array.isArray(category?.items)
        ? category.items
        : [];

      return categoryItems.flatMap((item, itemIndex) => {
        const parentItemName = sanitizeText(item?.itemName || "");
        const parentItemPrice = sanitizePrice(item?.price ?? "");

        if (!parentItemName) return [];

        const itemVariants = Array.isArray(item?.variants)
          ? item.variants
          : [];

        // Har variant ki separate Excel row
        if (itemVariants.length > 0) {
          return itemVariants.map((variant, variantIndex) => {
            const variantName = sanitizeText(
              variant?.itemName ||
                variant?.variantName ||
                variant?.name ||
                "",
            );

            const variantPrice = sanitizePrice(
              variant?.price ?? item?.price ?? "",
            );

            return {
              id: `${categoryIndex}-${itemIndex}-${variantIndex}`,

              itemName: parentItemName,
              itemDescription: "",
              photo: "",

              category: categoryName,
              price: variantPrice,
              discountPrice: "",

              variantName: variantName || "Default",
              iconDish: "Y",

              rackNumber: "",
              expiryDate: "",

              home_delivery_price: "",
              takeaway_price: "",
              dining_price: "",
              room_order_price: "",
              car_order_price: "",

              status: "Y",
              hasVariant: true,
            };
          });
        }

        // Variant nahi hai to Default row
        return [
          {
            id: `${categoryIndex}-${itemIndex}-default`,

            itemName: parentItemName,
            itemDescription: "",
            photo: "",

            category: categoryName,
            price: parentItemPrice,
            discountPrice: "",

            variantName: "Default",
            iconDish: "Y",

            rackNumber: "",
            expiryDate: "",

            home_delivery_price: "",
            takeaway_price: "",
            dining_price: "",
            room_order_price: "",
            car_order_price: "",

            status: "Y",
            hasVariant: false,
          },
        ];
      });
    });
  } catch (error) {
    console.error("Extract response parse error:", error);
    toast.error("Unable to parse extracted image response");
    return [];
  }
};

  const handleExtractImageUpload = async (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file");
      e.target.value = "";
      return;
    }

    try {
      setExtractingImage(true);

      const result = await extractChaafoMenuFromImage(file);

      if (!result) return;

      const extractedItems = parseExtractApiResponse(result);

      if (!extractedItems.length) {
        toast.error("No catalogue items found in image");
        return;
      }

      setExtractedCatalogueItems((prev) => {
        const oldItems = Array.isArray(prev) ? prev : [];
        return oldItems.concat(extractedItems);
      });

      setUploadedExtractImageNames((prev) => {
        const oldNames = Array.isArray(prev) ? prev : [];
        return oldNames.concat(file.name);
      });

      setExtractErrors([]);

      toast.success(`${extractedItems.length} items extracted from image`);
    } catch (error) {
      console.error("Extract image error:", error);
      toast.error("Failed to extract items from image");
    } finally {
      setExtractingImage(false);
      e.target.value = "";
    }
  };

  const handleBulkFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (evt) => {
      try {
        const text = String(evt.target?.result || "");

        const rows = parseCSV(text);

        if (!rows.length) {
          setCsvErrors([]);
          setBulkItems([]);
          toast.error("CSV file is empty");
          return;
        }

        const headers = rows[0].map(normalizeHeader);
        const dataRows = rows.slice(1);

        const items = dataRows
          .map((cols) => {
            const row = {};

            headers.forEach((key, i) => {
              row[key] = clean(cols[i] || "");
            });

            const rawItemName = getValue(row, ["itemName", "item name"]);

            const price = normalizePrice(getValue(row, ["price"]));
            const rawDiscountPrice = normalizePrice(
              getValue(row, [
                "discountPrice",
                "discount_price",
                "discount price",
              ]),
            );

            const numericPrice = Number(price || 0);
            const numericDiscountPrice = Number(rawDiscountPrice || 0);

            // In this CSV, discountPrice = 0 means no discount.
            // Do not convert it into a 100% discount.
            const hasValidDiscount =
              numericDiscountPrice > 0 &&
              numericDiscountPrice < numericPrice;

            const discount = hasValidDiscount
              ? (numericPrice - numericDiscountPrice).toFixed(2)
              : "0";

            const discountPrice = hasValidDiscount
              ? String(numericDiscountPrice)
              : price;

            return {
              originalItemName: rawItemName,

              itemName: sanitizeText(rawItemName),

              itemDescription: sanitizeText(
                getValue(row, [
                  "itemDescription",
                  "item description",
                  "description",
                ]),
              ),

              // Do not sanitize URL with normal text sanitizer, otherwise https:// will break
              photo: clean(
                getValue(row, ["photo", "image", "imageUrl", "image url"]),
              ),

              category: sanitizeText(
                getValue(row, ["category", "categoryName", "category name"]),
              ),
              price: sanitizePrice(price),
              discount: sanitizePrice(discount),
              discountPrice: sanitizePrice(discountPrice),

              // The uploaded sheet uses "Regular" as the column header,
              // while its row values are Half / Full / Regular.
              variantName:
                sanitizeText(
                  getValue(row, [
                    "variantName",
                    "variant name",
                    "variant",
                    "variation",
                    "size",
                    "portion",
                    "regular",
                  ]),
                ) || "Default",

              iconDish: sanitizeAlphaNumeric(
                getValue(row, ["iconDish", "icon dish", "dish", "veg"]),
              ).toUpperCase(),

              rackNumber: sanitizeText(
                getValue(row, ["rackNumber", "rack number", "rack"]),
              ),
              expiryDate: normalizeExpiryDate(
                getValue(row, ["expiryDate", "expiry date"]),
              ),

              home_delivery_price: normalizePrice(
                getValue(row, [
                  "home_delivery_price",
                  "home delivery price",
                  "homeDeliveryPrice",
                ]),
              ),
              takeaway_price: normalizePrice(
                getValue(row, [
                  "takeaway_price",
                  "takeaway price",
                  "takeawayPrice",
                ]),
              ),
              dining_price: normalizePrice(
                getValue(row, ["dining_price", "dining price", "diningPrice"]),
              ),
              room_order_price: normalizePrice(
                getValue(row, [
                  "room_order_price",
                  "room order price",
                  "roomOrderPrice",
                ]),
              ),
              car_order_price: normalizePrice(
                getValue(row, [
                  "car_order_price",
                  "car order price",
                  "carOrderPrice",
                ]),
              ),

              status: "Y",
              ratings: "0",
              customizations: "[]",
            };
          })
          .filter((item) => {
            return (
              item.itemName ||
              item.itemDescription ||
              item.photo ||
              item.category ||
              item.price
            );
          });

        if (!items.length) {
          setCsvErrors([]);
          setBulkItems([]);
          toast.error("No valid items found in CSV");
          return;
        }

        const validationErrors = validateBulkItems(items);

        setCsvErrors(validationErrors);
        setBulkItems(items);

        console.log("Parsed CSV Items:", items);
        console.table(items);

        if (validationErrors.length > 0) {
          toast.warning(
            `${validationErrors.length} CSV issues found. Check below.`,
          );
        } else {
          toast.success(`CSV is valid. Parsed ${items.length} items safely.`);
        }
      } catch (error) {
        console.error("CSV parse error:", error);
        setCsvErrors([
          "CSV file could not be parsed. Please check the format.",
        ]);
        setBulkItems([]);
        toast.error("CSV file could not be parsed. Please check the format.");
      } finally {
        e.target.value = "";
      }
    };

    reader.onerror = () => {
      setCsvErrors(["Unable to read file"]);
      toast.error("Unable to read file");
    };

    reader.readAsText(file, "UTF-8");
  };

  const csvEscape = (value) => {
    const str = clean(value);

    // In CSV, double quote must become two double quotes
    const escaped = str.replace(/"/g, '""');

    // Always wrap in quotes so comma, slash, brackets, symbols, etc. stay safe
    return `"${escaped}"`;
  };

  const handleDownloadSample = () => {
    const headers = [
      "itemName",
      "itemDescription",
      "photo",
      "category",
      "price",
      "discountPrice",
      "variantName",
      "iconDish",
      "rackNumber",
      "expiryDate",
      "home_delivery_price",
      "takeaway_price",
      "dining_price",
      "room_order_price",
      "car_order_price",
    ];

    const sampleRow = [
      "Margherita Pizza",
      "Classic cheese pizza",
      "https://example.com/image.jpg",
      "Food",
      "199",
      "179",
      "Regular",
      "Y",
      "1",
      "2025/12/31",
      "189",
      "179",
      "199",
      "209",
      "219",
    ];

    const csv =
      "\uFEFF" +
      headers.map(csvEscape).join(",") +
      "\n" +
      sampleRow.map(csvEscape).join(",");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "catalogue_sample.csv");

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

 const handleDownloadBulkExcel = async () => {
   try {
     const data = await fetchSizeMaster("0");

     if (!data || data.length === 0) {
       toast.error("No size master data available to download");
       return;
     }

     exportAnyJsonToExcel({
       data,
       filePrefix: "Size_Master",
       sheetName: "Size Master",
     });

     toast.success(`${data.length} size records downloaded successfully`);
   } catch (error) {
     console.error("Download size master Excel error:", error);
     toast.error("Failed to download size master Excel");
   }
 };

  const handleSingleSave = async () => {
    try {
      const selectedVariant = variants.find(
        (v) => String(v.variant_id) === String(formData.variant),
      );

      const payload = {
        userid: userID,

        items: [
          {
            itemName: clean(formData.itemName),
            itemId: "",
            status: formData.status,

            itemDescription: clean(formData.description),
            dish: clean(formData.tags),

            category: clean(formData.categoryInput || formData.category),
            catId: "",

            price: clean(formData.price),
            discount: clean(formData.discount || "0"),
            discountPrice: clean(formData.discountedPrice),

            home_delivery_price: clean(formData.homeDeliveryPrice),
            takeaway_price: clean(formData.takeawayPrice),
            dining_price: clean(formData.diningPrice),
            room_order_price: clean(formData.roomOrderPrice),
            car_order_price: clean(formData.carOrderPrice),

            expiryDate: clean(formData.expiryDate?.replace(/-/g, "/")),

            rackNumber: clean(formData.rack),

            //  dynamic variant
            variantId: clean(formData.variant),
            variantName: clean(selectedVariant?.variant_title),

            photo: clean(formData.photo),
            iconDish: clean(formData.vegIcon),

            barcodeNumber: clean(formData.barcode),

            ratings: "0",
            customizations: "[]",

            categoryDescription: "",
            itemNameTrans: "",
            itemDescriptionTrans: "",
            position: "",
            sizeTrans: "",
            subCategory: "",
          },
        ],
      };

      const success = await insertCatalogItem(payload);

      if (success) navigate(-1);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSafeBulkSubmit = async () => {
    if (csvErrors.length > 0) {
      toast.error("Please fix CSV validation issues before upload.");
      return;
    }

    if (!bulkItems.length) {
      toast.error("Please select and validate CSV file first.");
      return;
    }

    const result = await handleBulkSubmit();

    if (result?.success) {
      // Catalogue screen mounts again and fetches the newly inserted items.
      navigate(-1);
    }
  };

  const flattenObject = (obj, parentKey = "", res = {}) => {
    for (const key in obj) {
      const propName = parentKey ? `${parentKey}_${key}` : key;

      if (Array.isArray(obj[key])) {
        obj[key].forEach((item, index) => {
          if (typeof item === "object" && item !== null) {
            flattenObject(item, `${propName}_${index}`, res);
          } else {
            res[`${propName}_${index}`] = item;
          }
        });
      } else if (typeof obj[key] === "object" && obj[key] !== null) {
        flattenObject(obj[key], propName, res);
      } else {
        res[propName] = obj[key];
      }
    }

    return res;
  };

  const exportAnyJsonToExcel = ({
    data = [],
    filePrefix = "Export",
    sheetName = "Sheet1",
  }) => {
    if (!Array.isArray(data) || data.length === 0) {
      toast.error("No data available to export");
      return;
    }

    const flatData = data.map((item) => flattenObject(item));

    const worksheet = XLSX.utils.json_to_sheet(flatData, {
      skipHeader: false,
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const dateStr = new Date().toLocaleString("en-IN").replace(/[\/,: ]/g, "_");

    const fileName = `${filePrefix}_${dateStr}.xlsx`;

    saveAs(blob, fileName);
  };

const handleCreateExtractedCatalogueItems = () => {
  if (!extractedCatalogueItems.length) {
    toast.error("No extracted items found");
    return;
  }

  const excelData = extractedCatalogueItems.map((item) => {
    const extractedPrice = item.price || "";

    return {
      itemName: item.itemName || "",
      itemDescription: item.itemDescription || "",
      photo: item.photo || "",
      category: item.category || "",

      price: extractedPrice,
      discountPrice: item.discountPrice || "",

      variantName: item.variantName || "Default",
      iconDish: item.iconDish || "Y",

      rackNumber: item.rackNumber || "",
      expiryDate: item.expiryDate || "",

      // Same extracted price in all order types
      home_delivery_price: extractedPrice,
      takeaway_price: extractedPrice,
      dining_price: extractedPrice,
      room_order_price: extractedPrice,
      car_order_price: extractedPrice,
    };
  });

  exportAnyJsonToExcel({
    data: excelData,
    filePrefix: "Extracted_Catalogue_Items",
    sheetName: "Catalogue Items",
  });

  toast.success(
    `${excelData.length} catalogue rows downloaded successfully`,
  );
};

const filteredCategories = categories.filter((c) =>
  c.toLowerCase().includes(categorySearch.toLowerCase())
);

const filteredSubcategories = [
  ...new Map(
    subCategories
      ?.filter(
        (s) =>
          s.subCategory &&
          s.subCategory.trim() !== "" &&
          (!formData.category || s.category === formData.category) &&
          s.subCategory
            .toLowerCase()
            .includes(subcategorySearch.toLowerCase()),
      )
      .map((item) => [item.subCategory.trim(), item]),
  ).values(),
];

  return (
    <>
      <div className="p-6 h-full flex flex-col overflow-hidden montserrat-font-1">
        {/* HEADER */}
        <div className="sticky top-0 z-30 mb-4">
          <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-5 rounded-xl flex justify-between items-center shadow">
            <div>
              <h1 className="text-2xl font-bold montserrat-font-1">
                {mode === "single"
                  ? "Add Single Catalogue Item"
                  : mode === "bulk"
                    ? "Bulk Upload"
                    : "Extract Catalogue From Image"}
              </h1>
              <p className="text-xs opacity-80 tracking-wider">
                CATALOGUE ITEMS · CONTROL PANEL
              </p>
            </div>

            {/* TOGGLE BUTTON */}
            <div className="flex items-center gap-3">
              <div className="flex bg-white rounded-full p-1">
                <button
                  onClick={() => setMode("single")}
                  className={`px-4 py-1 text-sm rounded-full transition ${
                    mode === "single"
                      ? "bg-green-600 text-white"
                      : "text-green-600"
                  }`}
                >
                  Single
                </button>

                <button
                  onClick={() => setMode("bulk")}
                  className={`px-4 py-1 text-sm rounded-full transition ${
                    mode === "bulk"
                      ? "bg-green-600 text-white"
                      : "text-green-600"
                  }`}
                >
                  Bulk
                </button>

                <button
                  onClick={() => setMode("extract")}
                  className={`px-4 py-1 text-sm rounded-full transition ${
                    mode === "extract"
                      ? "bg-green-600 text-white"
                      : "text-green-600"
                  }`}
                >
                  Extract
                </button>
              </div>

              <input
                ref={extractImageInputRef}
                type="file"
                accept="image/*"
                onChange={handleExtractImageUpload}
                className="hidden"
              />

              <button
                onClick={() => navigate(-1)}
                className="bg-white text-green-600 px-3 py-1 rounded-md text-sm"
              >
                Back
              </button>
            </div>
          </div>
        </div>

        {/* BODY */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="bg-white mt-4 p-6 rounded-xl shadow space-y-5">
            {mode === "single" && (
              <>
                {/* ROW 1 */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="label">Item Name</label>
                    <input
                      name="itemName"
                      className="input"
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label className="label">Status</label>
                    <select
                      name="status"
                      className="input"
                      onChange={handleChange}
                    >
                      <option value="Y">Active (Y)</option>
                      <option value="N">Inactive (N)</option>
                    </select>
                  </div>

                  <div>
                    <label className="label">Ratings</label>
                    <input
                      name="rating"
                      className="input"
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* DESCRIPTION */}
                <div>
                  <label className="label">Description</label>
                  <textarea
                    name="description"
                    className="input w-full"
                    onChange={handleChange}
                  />
                </div>

                {/* TAG + IMAGE */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="label">Dish / Tags</label>
                    <input
                      name="tags"
                      className="input"
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label className="label">Veg/Non-Veg-Icon</label>
                    <select
                      name="vegIcon"
                      className="input"
                      onChange={handleChange}
                    >
                      <option value="">None</option>
                      <option value="Y">Veg (Y)</option>
                      <option value="N">Non-Veg (N)</option>
                    </select>
                  </div>

                  <div>
                    <label className="label">Photo</label>

                    <input
                      type="file"
                      accept="image/*"
                      className="input"
                      onChange={handleImageChange}
                    />

                    {/* PREVIEW */}
                    {formData.photo && (
                      <div className="mt-2 relative w-24 h-24">
                        <img
                          src={formData.photo}
                          alt="preview"
                          className="w-full h-full object-cover rounded-lg border"
                        />

                        {/* REMOVE BUTTON */}
                        <button
                          onClick={() =>
                            setFormData((prev) => ({ ...prev, photo: "" }))
                          }
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* CATEGORY */}
                <div className="grid grid-cols-2 gap-4">
                  {/* CATEGORY */}
                  <div>
                    <label className="label">Category</label>

                    <div className="relative mb-2">
                      <div
                        onClick={() =>
                          !formData.categoryInput &&
                          setShowCategoryDropdown((prev) => !prev)
                        }
                        className={`input flex justify-between items-center cursor-pointer ${
                          formData.categoryInput
                            ? "bg-gray-100 cursor-not-allowed"
                            : ""
                        }`}
                      >
                        <span
                          className={
                            formData.category ? "text-black" : "text-gray-400"
                          }
                        >
                          {formData.category || "Select Category"}
                        </span>

                        <ChevronDown
                          size={18}
                          className={`transition-transform ${
                            showCategoryDropdown ? "rotate-180" : ""
                          }`}
                        />
                      </div>

                      {showCategoryDropdown && (
                        <div className="absolute z-50 mt-1 w-full rounded-lg border bg-white shadow-lg">
                          <div className="max-h-60 overflow-y-auto">
                            {filteredCategories.length ? (
                              filteredCategories.map((category) => (
                                <button
                                  key={category}
                                  type="button"
                                  onClick={() => {
                                    setFormData((prev) => ({
                                      ...prev,
                                      category,
                                    }));
                                    setCategorySearch("");
                                    setShowCategoryDropdown(false);
                                  }}
                                  className="w-full px-3 py-2 text-left hover:bg-green-50"
                                >
                                  {category}
                                </button>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-gray-400 text-sm">
                                No category found
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/*  Manual Input */}
                    <input
                      type="text"
                      placeholder="Or type category"
                      className="input"
                      value={formData.categoryInput || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          categoryInput: e.target.value,
                          category: "", // disable dropdown
                        }))
                      }
                    />
                  </div>

                  {/* SUBCATEGORY */}
                  <div>
                    <label className="label">Subcategory</label>

                    <div className="relative mb-2">
                      <div
                        onClick={() =>
                          !formData.subcategoryInput &&
                          setShowSubcategoryDropdown((prev) => !prev)
                        }
                        className={`input flex justify-between items-center cursor-pointer ${
                          formData.subcategoryInput
                            ? "bg-gray-100 cursor-not-allowed"
                            : ""
                        }`}
                      >
                        <span
                          className={
                            formData.subcategory
                              ? "text-black"
                              : "text-gray-400"
                          }
                        >
                          {formData.subcategory || "Select Subcategory"}
                        </span>

                        <ChevronDown
                          size={18}
                          className={`transition-transform ${
                            showSubcategoryDropdown ? "rotate-180" : ""
                          }`}
                        />
                      </div>

                      {showSubcategoryDropdown && (
                        <div className="absolute z-50 mt-1 w-full rounded-lg border bg-white shadow-lg">
                          <div className="max-h-60 overflow-y-auto">
                            {filteredSubcategories.length ? (
                              filteredSubcategories.map((item) => (
                                <button
                                  key={item.subCategory}
                                  type="button"
                                  onClick={() => {
                                    setFormData((prev) => ({
                                      ...prev,
                                      subcategory: item.subCategory,
                                    }));

                                    setSubcategorySearch("");
                                    setShowSubcategoryDropdown(false);
                                  }}
                                  className="w-full px-3 py-2 text-left hover:bg-green-50"
                                >
                                  {item.subCategory}
                                </button>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-sm text-gray-400">
                                No subcategory found
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/*  Manual Input */}
                    <input
                      type="text"
                      placeholder="Or type subcategory"
                      className="input"
                      value={formData.subcategoryInput || ""}
                      disabled={!!formData.subcategory}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          subcategoryInput: e.target.value,
                          subcategory: "",
                        }))
                      }
                    />
                  </div>
                </div>

                {/* VARIANT + UOM + SIZE + RACK */}
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <label className="label">Variant</label>
                    <select
                      name="variant"
                      className="input"
                      onChange={handleChange}
                      value={formData.variant}
                    >
                      <option value="">Select</option>
                      {variants?.map((v, i) => (
                        <option key={i} value={v.variant_id}>
                          {v.variant_title}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="label">UOM</label>
                    <select
                      name="uom"
                      className="input"
                      value={formData.uom}
                      onChange={handleUomChange}
                      disabled={uomLoading}
                    >
                      <option value="">
                        {uomLoading ? "Loading..." : "Select UOM"}
                      </option>

                      {uomOptions?.map((item, index) => (
                        <option key={index} value={item.uom_code}>
                          {item.uom_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="label">Size</label>
                    <select
                      name="size"
                      className="input"
                      value={formData.size}
                      onChange={handleChange}
                      disabled={!formData.uom || sizeLoading}
                    >
                      <option value="">
                        {sizeLoading ? "Loading..." : "Select Size"}
                      </option>

                      {sizeOptions?.map((item, index) => (
                        <option key={index} value={getOptionValue(item)}>
                          {getOptionLabel(item)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="label">Rack</label>
                    <select
                      name="rack"
                      className="input"
                      onChange={handleChange}
                      value={formData.rack}
                    >
                      <option value="">Select</option>
                      {racks?.map((r, i) => (
                        <option key={i} value={r.rackNumber}>
                          {r.rackNumber}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* PRICE */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="label">Price</label>
                    <input
                      name="price"
                      className="input"
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label className="label">Discount</label>
                    <input
                      name="discount"
                      className="input"
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label className="label">Discounted Price</label>
                    <input
                      name="discountedPrice"
                      className="input"
                      value={formData.discountedPrice}
                      readOnly
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="label">Home Delivery Price</label>
                    <input
                      name="homeDeliveryPrice"
                      className="input"
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label className="label">Takeaway Price</label>
                    <input
                      name="takeawayPrice"
                      className="input"
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label className="label">Dining Price</label>
                    <input
                      name="diningPrice"
                      className="input"
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Room Order Price</label>
                    <input
                      name="roomOrderPrice"
                      className="input"
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label className="label">Car Order Price</label>
                    <input
                      name="carOrderPrice"
                      className="input"
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* EXTRA */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Barcode</label>
                    <input
                      name="barcode"
                      className="input"
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label className="label">Expiry Date</label>
                    <input
                      type="date"
                      name="expiryDate"
                      className="input"
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* BUTTON */}
                <div className="flex justify-end">
                  <button
                    onClick={handleSingleSave}
                    className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700"
                  >
                    Save Item
                  </button>
                </div>
              </>
            )}

            {/* BULK MODE */}
            {mode === "bulk" && (
              <div className="space-y-5">
                {/* INFO TEXT */}
                <div className="text-sm text-gray-700">
                  Download the sample CSV, fill it in Excel and upload again.
                  For bulk upload,{" "}
                  <span className="font-semibold">
                    images are not auto-uploaded – please provide ready image
                    URLs in the <span className="text-pink-600">photo</span>{" "}
                    column.
                  </span>
                </div>

                {/* RULES BOX */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm">
                  <p className="font-semibold mb-2">CSV Columns & Rules</p>

                  <ul className="list-disc pl-5 space-y-1 text-gray-700">
                    <li>
                      <span className="text-red-500">itemName</span> – Item name
                      (required).
                    </li>
                    <li>
                      <span className="text-red-500">itemDescription</span> –
                      Short description.
                    </li>
                    <li>
                      <span className="text-red-500">photo</span> – Full image
                      URL (e.g. https://...)
                    </li>
                    <li>
                      <span className="text-red-500">category</span> – Category
                      name (e.g. Food, Grocery)
                    </li>
                    <li>
                      <span className="text-red-500">price</span> – MRP / base
                      price
                    </li>
                    <li>
                      <span className="text-red-500">discountPrice</span> –
                      Final selling price
                      <br />
                      <span className="text-gray-500 text-xs">
                        Discount auto-calculated as price - discountPrice
                      </span>
                    </li>
                    <li>
                      <span className="text-red-500">variantName</span> – Half /
                      Full / Regular. The old <b>Regular</b> header is also
                      supported.
                    </li>

                    <li>
                      <span className="text-red-500">iconDish</span> – Veg /
                      Non-Veg:
                      <ul className="list-disc pl-5">
                        <li>Y = Veg</li>
                        <li>N = Non-Veg</li>
                      </ul>
                    </li>

                    <li>
                      <span className="text-red-500">rackNumber</span> – e.g. 1,
                      2A
                    </li>
                    <li>
                      <span className="text-red-500">expiryDate</span> – format
                      YYYY/MM/DD
                    </li>
                  </ul>

                  <p className="text-xs text-gray-500 mt-2">
                    Other fields (status, ratings, subCategory, etc.) are
                    auto-handled.{" "}
                    <span className="text-red-500">customizations</span> is
                    always sent as empty array [].
                  </p>
                </div>

                {/* DOWNLOAD BUTTON */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleDownloadSample}
                    className="border border-green-500 text-green-600 px-4 py-2 rounded-md text-sm hover:bg-green-50"
                  >
                    Download Sample CSV
                  </button>

                  <button
                    onClick={handleDownloadBulkExcel}
                    className="border border-blue-500 text-blue-600 px-4 py-2 rounded-md text-sm hover:bg-blue-50"
                  >
                    Download Excel
                  </button>
                </div>

                {/* FILE INPUT */}

                <div>
                  <label className="label">
                    Upload filled CSV (Excel export)
                  </label>
                  <input
                    type="file"
                    accept=".csv,text/csv,application/vnd.ms-excel"
                    className="input"
                    onChange={handleBulkFileChange}
                  />
                </div>

                {csvErrors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
                    <p className="font-semibold mb-2">CSV Validation Issues</p>

                    <ul className="list-disc pl-5 space-y-1">
                      {csvErrors.map((err, index) => (
                        <li key={index}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* PREVIEW TABLE */}

                {bulkItems.length > 0 && (
                  <div className="border rounded-lg overflow-hidden bg-white">
                    {/* TABLE HEADER + UPLOAD BUTTON */}
                    <div className="flex items-center justify-between gap-3 p-3 border-b bg-white">
                      <div>
                        <p className="text-sm font-semibold text-gray-800">
                          Parsed {bulkItems.length} items
                        </p>
                        <p className="text-xs text-gray-500">
                          Showing all uploaded Excel/CSV rows
                        </p>
                      </div>

                      <button
                        onClick={handleSafeBulkSubmit}
                        disabled={
                          bulkLoading ||
                          csvErrors.length > 0 ||
                          bulkItems.length === 0
                        }
                        className={`px-5 py-2 rounded-md text-sm text-white ${
                          bulkLoading ||
                          csvErrors.length > 0 ||
                          bulkItems.length === 0
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-green-500 hover:bg-green-600"
                        }`}
                      >
                        {bulkLoading ? "Uploading..." : "Upload Items"}
                      </button>
                    </div>

                    {/* FULL TABLE SCROLL */}
                    <div className="w-full overflow-x-auto overflow-y-auto max-h-[520px] border-t">
                      <div className="min-w-[2200px]">
                        <table className="w-full text-sm">
                          <thead className="bg-green-50 text-left sticky top-0 z-10">
                            <tr>
                              <th className="p-2 whitespace-nowrap">#</th>

                              <th className="p-2 whitespace-nowrap">
                                Item Name
                              </th>

                              <th className="p-2 whitespace-nowrap min-w-[350px]">
                                Description
                              </th>

                              <th className="p-2 whitespace-nowrap min-w-[220px]">
                                Category
                              </th>

                              <th className="p-2 whitespace-nowrap">Variant</th>

                              <th className="p-2 whitespace-nowrap">Price</th>
                              <th className="p-2 whitespace-nowrap">
                                Discount
                              </th>
                              <th className="p-2 whitespace-nowrap">
                                Final Price
                              </th>
                              <th className="p-2 whitespace-nowrap">HD</th>
                              <th className="p-2 whitespace-nowrap">TA</th>
                              <th className="p-2 whitespace-nowrap">DI</th>
                              <th className="p-2 whitespace-nowrap">RO</th>
                              <th className="p-2 whitespace-nowrap">CO</th>
                              <th className="p-2 whitespace-nowrap">Icon</th>
                              <th className="p-2 whitespace-nowrap">Rack</th>
                              <th className="p-2 whitespace-nowrap">Expiry</th>
                            </tr>
                          </thead>

                          <tbody>
                            {bulkItems.map((item, i) => (
                              <tr key={i} className="border-t hover:bg-gray-50">
                                <td className="p-2 whitespace-nowrap">
                                  {i + 1}
                                </td>

                                <td className="p-2 whitespace-nowrap min-w-[220px]">
                                  {item.itemName || "-"}
                                </td>

                                <td className="p-2 min-w-[350px] max-w-[450px] whitespace-normal">
                                  {item.itemDescription || "-"}
                                </td>

                                <td className="p-2 whitespace-nowrap min-w-[220px]">
                                  {item.category || "-"}
                                </td>

                                <td className="p-2 whitespace-nowrap font-semibold text-blue-700">
                                  {item.variantName || "Default"}
                                </td>

                                <td className="p-2 whitespace-nowrap">
                                  {item.price}
                                </td>

                                <td className="p-2 whitespace-nowrap">
                                  {item.discount}
                                </td>

                                <td className="p-2 whitespace-nowrap">
                                  {item.discountPrice}
                                </td>

                                <td className="p-2 whitespace-nowrap">
                                  {item.home_delivery_price}
                                </td>

                                <td className="p-2 whitespace-nowrap">
                                  {item.takeaway_price}
                                </td>

                                <td className="p-2 whitespace-nowrap">
                                  {item.dining_price}
                                </td>

                                <td className="p-2 whitespace-nowrap">
                                  {item.room_order_price}
                                </td>

                                <td className="p-2 whitespace-nowrap">
                                  {item.car_order_price}
                                </td>

                                <td className="p-2 whitespace-nowrap">
                                  {item.iconDish}
                                </td>

                                <td className="p-2 whitespace-nowrap">
                                  {item.rackNumber}
                                </td>

                                <td className="p-2 whitespace-nowrap">
                                  {item.expiryDate}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Extract MODE */}
            {mode === "extract" && (
              <div className="space-y-5">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Extract Catalogue Items
                    </h2>
                    <p className="text-xs text-gray-500">
                      Upload menu image and show extracted items in table format
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => extractImageInputRef.current?.click()}
                      disabled={extractingImage}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm disabled:opacity-60"
                    >
                      {extractingImage
                        ? "Extracting..."
                        : uploadedExtractImageNames.length > 0
                          ? "Add More Image"
                          : "Upload Image"}
                    </button>

                    <button
                      type="button"
                      onClick={handleCreateExtractedCatalogueItems}
                      disabled={!extractedCatalogueItems.length}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm disabled:opacity-60"
                    >
                      Create
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setExtractedCatalogueItems([]);
                        setUploadedExtractImageNames([]);
                      }}
                      disabled={!extractedCatalogueItems.length}
                      className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md text-sm disabled:opacity-60"
                    >
                      Clear
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto border rounded-lg">
                  <table className="min-w-full text-sm">
                    <thead className="bg-green-50 text-left text-gray-700">
                      <tr>
                        <th className="px-4 py-3 border-b">#</th>
                        <th className="px-4 py-3 border-b">Category</th>
                        <th className="px-4 py-3 border-b">Item Name</th>
                        <th className="px-4 py-3 border-b">Price</th>
                        <th className="px-4 py-3 border-b">Variant</th>
                      </tr>
                    </thead>

                    <tbody>
                      {extractedCatalogueItems.length > 0 ? (
                        extractedCatalogueItems.map((item, index) => (
                          <tr
                            key={item.id || index}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-4 py-3 border-b">{index + 1}</td>

                            <td className="px-4 py-3 border-b font-medium">
                              {item.category || "-"}
                            </td>

                            <td className="px-4 py-3 border-b">
                              {item.itemName || "-"}
                            </td>

                            <td className="px-4 py-3 border-b">
                              {item.price ? `₹${item.price}` : "N/A"}
                            </td>

                            <td className="px-4 py-3 border-b">
                              {item.hasVariant ? (
                                <span className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                                  {item.variantName}
                                </span>
                              ) : (
                                <span className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                                  Default
                                </span>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-4 py-10 text-center text-sm text-gray-500"
                          >
                            No extracted items yet. Click Upload Image to
                            extract catalogue.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        {/*  COMMON STYLES */}
        <style>{`
  .input {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 14px;
  }
  .input:disabled {
    background-color: #e5e7eb;   /* gray-200 */
    cursor: not-allowed;
  }
  .label {
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 4px;
    display: block;
  }
`}</style>
      </div>
    </>
  );
};

export default AddCatalogueItem;
